import { useState } from 'react';

import { toggleAutoRenewalStatusAction } from 'actions/toggleAutoRenewalStatusAction';
import { FullPolicyData } from 'interfaces/customerDataTypes';
import { useAppDispatch, useAppSelector } from 'store/hooks';

import { Toggle } from '../toggle';
import { ToggleMessages } from '../toggleMessages';
import type { ToggleMessageList } from '../toggleMessages/toggleMessages';
import { ToggleModal } from '../toggleModal';

import './autoRenewalSettings.scss';

interface AutoRenewalSettingsProps {
  selectedPolicy: FullPolicyData;
}

const WILL_AUTO_RENEW: ToggleMessageList[] = [
  {
    icon: 'shield',
    text: "You'll remain protected for the next policy year"
  },
  {
    icon: 'refresh',
    text: "Your policy will renew automatically if you're happy with the price and cover - no need to give us a call"
  },
  {
    icon: 'calendar-plus',
    text: "We'll write to you around 30 days before renewal and you'll have an additional 14-day cooling off period to make changes to your policy"
  }
];

const NOT_AUTO_RENEW: ToggleMessageList[] = [
  {
    icon: 'cross',
    text: "Cover will end at the renewal date, and you'll no longer be protected"
  },
  {
    icon: 'cross',
    text: 'Your policy will not renew automatically unless you get in touch with us'
  }
];

export const AutoRenewalSettings = ({ selectedPolicy }: AutoRenewalSettingsProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const [show, setShow] = useState(false);

  const { customerRenewalOptOut, allowCustomerPreferenceAmendment } = selectedPolicy;

  const isToggleLoading = useAppSelector((state) => state.customerData.toggleAutoRenewLoading);
  const isToggleError = useAppSelector((state) => state.customerData.toggleAutoRenewalError);
  const authentication = useAppSelector((state) => state.authentication);
  const { email, isAgent } = authentication;

  const handleClose = () => {
    setShow(false);
  };

  const toggleRenewal = () => {
    dispatch(
      toggleAutoRenewalStatusAction({
        isAutoRenewal: customerRenewalOptOut,
        selectedPolicy,
        username: email,
        isAgent
      })
    );
    setShow(false);
  };

  const handleAutoRenewToggleClick = () => {
    if (!customerRenewalOptOut) {
      setShow(true);
    } else {
      toggleRenewal();
    }
  };

  const renewalMessageList = customerRenewalOptOut ? NOT_AUTO_RENEW : WILL_AUTO_RENEW;
  const renewalStyles = customerRenewalOptOut ? 'not-auto-renew-messages' : 'will-auto-renew-messages';
  return (
    <div className="auto-renewal-settings" id="auto-renewal-preferences">
      <h4>Auto-renewal preference</h4>
      <ToggleMessages messageList={renewalMessageList} styles={renewalStyles} messageTitle="When it's time to renew" />
      <Toggle
        isToggleOn={!customerRenewalOptOut}
        isToggleLoading={isToggleLoading}
        isToggleError={isToggleError}
        handleToggleClick={handleAutoRenewToggleClick}
        toggleLoadingText={`Please wait while we opt you ${customerRenewalOptOut ? 'in' : 'out'}`}
        toggleLabel="Automatically renew this policy"
        isToggleEnabled={allowCustomerPreferenceAmendment}
        toggleName="auto-renew-switch"
      />
      <ToggleModal
        show={show}
        handleConfirm={toggleRenewal}
        handleClose={handleClose}
        modalTitle="TURN OFF AUTO-RENEWAL"
        modalText="Your cover will end at the policy renewal date. Are you sure you want to turn off auto-renewal?"
      />
    </div>
  );
};
